"use client";
import clsx from "clsx";
import { useRef } from "react";

import Link from "next/link";
import type {
  ProductCard as PC,
  ProductCard,
} from "@/lib/centra/formatters/formatProductCards/formatProductCard";
import { useLocale } from "next-intl";
import Image from "next/image";
import { getProductLink } from "../../ProductProvider";
import ProductDescription from "../ProductDescription/ProductDescription";
import ProductLabels from "../ProductLabels/ProductLabels";
import ProductCardActions from "./ProductCardActions";
import styles from "./product-card.module.css";

const ProductCard = ({
  product,
  hex,
  related = false,
  fullWidthMobile,
  variant = "default",
}: {
  product: PC;
  hex?: string;
  related?: boolean;
  className?: string;
  fullWidthMobile?: boolean;
  variant?: "simple" | "default";
}) => {
  const locale = useLocale();
  const linkRef = useRef<HTMLAnchorElement>(null!);

  const slug = product?.core?.slug ?? "#";

  if (!product) {
    return null;
  }

  const image = product?.gallery?.image;
  const imageAlt = product?.gallery?.altImage;

  const name = product.core?.name;

  return (
    <article>
      <div className={styles.card}>
        <Link
          ref={linkRef}
          title={`${name}`}
          href={getProductLink({ locale, slug })}
        >
          <div
            className={clsx(styles.card__image, { [styles.has_alt]: imageAlt })}
          >
            {variant !== "simple" && (
              <ProductLabels
                className={styles.labels}
                isDark={false}
                labels={product?.labels}
              />
            )}
            <div className={styles.card__image__inner}>
              {image && (
                <Image
                  src={image.href}
                  alt={image.alt}
                  width={402}
                  height={505}
                  loading="eager"
                  priority
                  className={styles.product__image}
                />
              )}
              {imageAlt && (
                <Image
                  src={imageAlt.href}
                  alt={imageAlt.alt}
                  width={402}
                  height={505}
                  loading="eager"
                  fetchPriority="high"
                  className={clsx(
                    styles.product__image,
                    styles.product__image_alt,
                  )}
                />
              )}
            </div>
          </div>
        </Link>
        {variant !== "simple" && (
          <ProductDescription
            hex={hex}
            id={product?.id || ""}
            product={product}
            fullWidthMobile={fullWidthMobile}
          />
        )}
      </div>
      {related && <ProductCardActions product={product} />}
    </article>
  );
};

export default ProductCard;
